import { FC, ReactNode } from 'react'
import { alpha } from '@mui/material/styles'
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'
import R4RIcon, { R4RIconName } from 'components/R4RIcon'
import { WidgetCard, WidgetCardHeader, WidgetCardBody } from './WidgetCard'
import { useLeaseContract, useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'

const ContactChip: FC<{ icon: R4RIconName; href: string; children: ReactNode }> = ({
  icon,
  href,
  children
}) => {
  const theme = useTheme()
  return (
    <Box
      component='a'
      href={href}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        backgroundColor: alpha(theme.r4r.azure, 0.1),
        border: `1px solid ${alpha(theme.r4r.azure, 0.3)}`,
        padding: '7px 13px',
        textDecoration: 'none',
        color: theme.r4r.navy
      }}>
      <R4RIcon name={icon} size={14} />
      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'inherit' }}>
        {children}
      </Typography>
    </Box>
  )
}

const WidgetConsultant: FC = () => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { leaseContractsList } = useLeaseContractsList()

  const activeLeases = leaseContractsList.filter(({ leaseStatus }) => leaseStatus === 0)
  const itemWithHighestId = activeLeases.reduce((max, item) => {
    return item?.id > max?.id ? item : max
  }, activeLeases[0])

  const { leaseContract, isLoading } = useLeaseContract(itemWithHighestId?.id)

  if (!activeLeases.length) {
    return null
  }

  const owner = leaseContract?.owner
  const ownerName = [owner?.firstName, owner?.lastName].filter(Boolean).join(' ')
  const ownerPhone = owner?.mobile || owner?.phone
  const ownerEmail = owner?.email

  if (isDesktop) {
    return (
      <WidgetCard sx={{ flex: 1, minWidth: 0 }}>
        <WidgetCardHeader
          icon='people'
          title={formatMessage({ id: 'consultantWidget.contacts', defaultMessage: 'Kontakty' })}
        />
        <WidgetCardBody sx={{ gap: '10px' }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box
                  sx={{
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: theme.r4r.gradientViolet,
                    color: '#fff',
                    flexShrink: 0
                  }}>
                  <R4RIcon name='person' size={20} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.ink }}>
                    {ownerName || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
                    {formatMessage({
                      id: 'consultantWidget.tenantCaretaker',
                      defaultMessage: 'Opiekun najemcy'
                    })}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {!!ownerPhone && (
                  <ContactChip icon='call' href={`tel:${ownerPhone}`}>
                    {ownerPhone}
                  </ContactChip>
                )}
                {!!ownerEmail && (
                  <ContactChip icon='mail' href={`mailto:${ownerEmail}`}>
                    {ownerEmail}
                  </ContactChip>
                )}
              </Box>
            </>
          )}
        </WidgetCardBody>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard sx={{ padding: '14px 18px 20px', gap: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
        <R4RIcon name='people' size={16} />
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
          {formatMessage({ id: 'consultantWidget.contacts', defaultMessage: 'Kontakty' })}
        </Typography>
      </Box>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              sx={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.r4r.gradientViolet,
                color: '#fff',
                flexShrink: 0
              }}>
              <R4RIcon name='person' size={20} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.ink }}>
                {ownerName || '-'}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
                {formatMessage({
                  id: 'consultantWidget.tenantCaretaker',
                  defaultMessage: 'Opiekun najemcy'
                })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            {!!ownerPhone && (
              <ContactChip icon='call' href={`tel:${ownerPhone}`}>
                {ownerPhone}
              </ContactChip>
            )}
            {!!ownerEmail && (
              <ContactChip icon='mail' href={`mailto:${ownerEmail}`}>
                {ownerEmail}
              </ContactChip>
            )}
          </Box>
        </>
      )}
    </WidgetCard>
  )
}

export default WidgetConsultant
